/**
 * The one place food gets in. Every path — search, barcode, meal photo,
 * description, nutrition label, quick add, recipes, saved meals, recents —
 * ends in the same two steps: pick an amount (FoodPortion) or review a draft
 * (FoodDraft), then log.
 *
 * Offline: search still finds your own foods, recents, recipes and saved meals
 * (they're local); USDA, barcode lookup and AI need a connection and say so
 * rather than spinning.
 */

import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { api, ApiError } from '../api/client'
import { db, type CustomFoodRow, type RecipeRow, type SavedMealRow } from '../db'
import { logFoods, touch, touchCustomFood } from '../db/mutations'
import { useCustomFoods, useProfile, useRecentFoods, useRecipes, useSavedMeals, type RecentFood } from '../db/queries'
import { useApp } from '../context'
import { mealForNow, mealNames, NOMINAL_G, refParts, sourceLabel, type FoodItem } from '../lib/foodItem'
import { LABEL, MEAL, prepareImage, ImageReadError } from '../lib/image'
import { fmtKcal, scale, sum } from '../lib/nutrients'
import { withTimeout } from '../lib/timeout'
import { CustomFoodEditor, type CustomFoodDraft } from './CustomFoodEditor'
import { FoodDraft, type Estimate } from './FoodDraft'
import { FoodPortion, type Portion } from './FoodPortion'
import { Sheet } from './Sheet'
import { subscribe } from '../sync/engine'

const Scanner = lazy(() => import('./Scanner').then((m) => ({ default: m.Scanner })))

type Mode =
  | { k: 'home' }
  | { k: 'search' }
  | { k: 'portion'; item: FoodItem }
  | { k: 'scan' }
  | { k: 'scan-miss'; code: string }
  | { k: 'describe'; text?: string }
  | { k: 'photo' }
  | { k: 'label'; barcode?: string | null }
  | { k: 'draft'; estimate: Estimate; source: 'ai_photo' | 'ai_text' }
  | { k: 'custom'; draft: CustomFoodDraft; note?: string | null }
  | { k: 'quick' }
  | { k: 'library' }
  | { k: 'meal'; meal: SavedMealRow }

const PENDING_KEY = 'macro.pendingEstimate'

export function customToItem(r: CustomFoodRow): FoodItem {
  return { ref: `custom:${r.id}`, source: 'custom', name: r.name, brand: r.brand, basis: r.basis,
    per100: r.per100 ?? {}, servings: r.servings ?? [], barcode: r.barcode }
}

export function recipeToItem(r: RecipeRow): FoodItem {
  const servingG = r.cooked_weight_g ? r.cooked_weight_g / r.servings : (r.total_grams ?? 0) / (r.servings || 1)
  return { ref: `recipe:${r.id}`, source: 'recipe', name: r.name, basis: 'g', per100: r.per100 ?? {},
    servings: servingG > 0 ? [{ label: '1 serving', grams: Math.round(servingG * 10) / 10, default: true }] : [] }
}

function recentToItem(r: RecentFood): FoodItem | null {
  const per100 = r.per100
  if (!per100) return null
  const servings = r.serving_label && r.grams && r.serving_qty
    ? [{ label: r.serving_label, grams: Math.round((r.grams / r.serving_qty) * 10) / 10, default: true }] : []
  // A restaurant item logged against its nominal weight stays serving-only.
  const nominal = r.source === 'restaurant' && !!r.grams && !!r.serving_qty
    && Math.abs(r.grams / r.serving_qty - NOMINAL_G) < 0.5
  return { ref: r.source_ref ? `${r.source}:${r.source_ref}` : null, source: r.source as FoodItem['source'],
    name: r.name, brand: r.brand, basis: 'g', per100, servings, nominal,
    lastGrams: r.grams, lastServing: r.serving_label, lastQty: r.serving_qty }
}

export function useOnline(): boolean {
  const [online, setOnline] = useState(true)
  useEffect(() => subscribe((s) => setOnline(s.online)), [])
  return online
}

function errMsg(e: unknown): string {
  if (e instanceof ApiError) return e.message
  if (e instanceof ImageReadError) return e.message
  if (e instanceof Error && e.name === 'TimeoutError') return 'That took too long. Try again.'
  return "Couldn't reach the server."
}

export function AddFood({ day, meal: meal0, initial, onClose }: {
  day: string; meal?: number; initial?: 'scan' | 'photo' | 'describe' | 'search' | 'quick'; onClose: () => void
}) {
  const profile = useProfile()
  const names = mealNames(profile?.meal_names)
  const [meal, setMeal] = useState(meal0 ?? mealForNow())
  const [stack, setStack] = useState<Mode[]>([{ k: initial ?? 'home' } as Mode])
  const [flash, setFlash] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const online = useOnline()

  const mode = stack[stack.length - 1]
  const push = (m: Mode) => { setError(null); setStack((s) => [...s, m]) }
  const back = () => { setError(null); setStack((s) => (s.length > 1 ? s.slice(0, -1) : s)) }
  const home = (msg?: string) => { setError(null); setStack([{ k: 'home' }]); if (msg) setFlash(msg) }

  useEffect(() => { if (flash) { const t = setTimeout(() => setFlash(null), 2500); return () => clearTimeout(t) } }, [flash])

  const logItem = async (item: FoodItem, p: Portion) => {
    setBusy(true)
    try {
      const { source, source_ref } = refParts(item)
      await logFoods([{ name: item.name, brand: item.brand, source, source_ref, grams: p.grams,
        serving_label: p.serving_label, serving_qty: p.serving_qty, per100: item.per100 }], day, meal)
      if (item.source === 'custom' && source_ref) void touchCustomFood(source_ref)
      if (item.source === 'recipe' && source_ref) void touch('recipe', source_ref)
      home(`Added ${item.name}`)
    } finally {
      setBusy(false)
    }
  }

  const titles: Record<Mode['k'], string> = {
    home: `Add to ${names[meal] ?? 'log'}`, search: 'Search', portion: 'How much?', scan: 'Scan barcode',
    'scan-miss': 'Not found', describe: 'Describe it', photo: 'Photo of your meal', label: 'Nutrition label',
    draft: 'Check the estimate', custom: 'Your food', quick: 'Quick add', library: 'Recipes & meals', meal: 'Saved meal',
  }

  return (
    <Sheet title={titles[mode.k]} onClose={onClose} onBack={stack.length > 1 ? back : null}>
      {error && <div className="error">{error}</div>}
      {flash && <p className="small" style={{ color: 'var(--accent)', margin: '.5rem 0 0' }}>✓ {flash}</p>}

      {mode.k === 'home' && (
        <Home names={names} meal={meal} setMeal={setMeal} online={online}
          onSearch={() => push({ k: 'search' })}
          onPick={(item) => push({ k: 'portion', item })}
          go={(k) => push({ k } as Mode)} />
      )}

      {mode.k === 'search' && (
        <Search online={online} onPick={async (item) => {
          if (item.source === 'usda' && item.ref && online) {
            try {
              const r = await api<{ food: FoodItem }>(`/foods/usda/${item.ref.split(':')[1]}`)
              if (r.food.servings.length) item = { ...item, servings: r.food.servings }
            } catch { /* portions are a nicety; grams always work */ }
          }
          push({ k: 'portion', item })
        }} onMeal={(m) => push({ k: 'meal', meal: m })} onDescribe={(text) => push({ k: 'describe', text })}
        onCreate={(name) =>
          push({ k: 'custom', draft: { name, basis: 'g', serving_label: '', serving_size: null, per100: {} } })} />
      )}

      {mode.k === 'portion' && (
        <FoodPortion item={mode.item} busy={busy} actionLabel={`Log to ${names[meal]}`}
          onSubmit={(p) => void logItem(mode.item, p)} />
      )}

      {mode.k === 'scan' && (
        <Suspense fallback={<p className="muted">Loading scanner…</p>}>
          <ScanStep online={online} onFound={(item) => push({ k: 'portion', item })}
            onMiss={(code) => push({ k: 'scan-miss', code })} setError={setError} />
        </Suspense>
      )}

      {mode.k === 'scan-miss' && (
        <div>
          <p>That barcode ({mode.code}) isn't in the food database yet.</p>
          <p className="muted small">Photograph the nutrition label and it's read for you and saved as your own food, bound to this barcode — so next time the scan just works.</p>
          <button style={{ width: '100%' }} disabled={!online} onClick={() => push({ k: 'label', barcode: mode.code })}>
            Photograph the label</button>
          <button className="secondary" style={{ width: '100%' }} onClick={() => push({ k: 'custom',
            draft: { name: '', basis: 'g', barcode: mode.code, serving_label: '', serving_size: null, per100: {} } })}>
            Type it in</button>
        </div>
      )}

      {(mode.k === 'describe' || mode.k === 'photo') && (
        <EstimateStep photo={mode.k === 'photo'} online={online} initialText={mode.k === 'describe' ? mode.text : undefined} busy={busy} setBusy={setBusy} setError={setError}
          onEstimate={(estimate, source) => push({ k: 'draft', estimate, source })} />
      )}

      {mode.k === 'draft' && (
        <FoodDraft estimate={mode.estimate} busy={busy} onLog={async (items, groupLabel) => {
          setBusy(true)
          try {
            await logFoods(items.map((i) => ({
              name: i.name, source: mode.source, grams: i.grams, serving_label: i.serving_desc ?? null,
              per100: i.per100, nutrients: i.nutrients, ai_confidence: i.confidence, notes: i.note ?? null,
            })), day, meal, groupLabel || 'Meal')
            try { sessionStorage.removeItem(PENDING_KEY) } catch { /* ignore */ }
            home(`Logged ${items.length} item${items.length === 1 ? '' : 's'}`)
          } finally { setBusy(false) }
        }} />
      )}

      {mode.k === 'label' && (
        <LabelStep online={online} barcode={mode.barcode} busy={busy} setBusy={setBusy} setError={setError}
          onRead={(draft, note) => push({ k: 'custom', draft, note })} />
      )}

      {mode.k === 'custom' && (
        <CustomFoodEditor initial={mode.draft} note={mode.note} onSaved={(row) => {
          setStack((s) => [...s.slice(0, -1), { k: 'portion', item: customToItem(row) }])
        }} />
      )}

      {mode.k === 'quick' && <QuickAdd busy={busy} onLog={async (name, n) => {
        setBusy(true)
        try {
          await logFoods([{ name: name || 'Quick add', source: 'quick', nutrients: n }], day, meal)
          home('Added')
        } finally { setBusy(false) }
      }} />}

      {mode.k === 'library' && <Library onRecipe={(r) => push({ k: 'portion', item: recipeToItem(r) })}
        onMeal={(m) => push({ k: 'meal', meal: m })} />}

      {mode.k === 'meal' && (
        <MealStep meal={mode.meal} busy={busy} onLog={async () => {
          setBusy(true)
          try {
            await logFoods((mode.meal.items ?? []).map((i) => ({
              name: i.name, brand: i.brand, source: (i.source as never) ?? 'meal', source_ref: i.source_ref,
              grams: i.grams, serving_label: i.serving_label, serving_qty: i.serving_qty,
              per100: i.per100, nutrients: i.nutrients })), day, meal, mode.meal.name)
            void touch('saved_meal', mode.meal.id)
            home(`Logged ${mode.meal.name}`)
          } finally { setBusy(false) }
        }} />
      )}
    </Sheet>
  )
}

// --- home ---------------------------------------------------------------------

function Home({ names, meal, setMeal, online, onSearch, onPick, go }: {
  names: string[]; meal: number; setMeal: (m: number) => void; online: boolean
  onSearch: () => void; onPick: (i: FoodItem) => void; go: (k: Mode['k']) => void
}) {
  const recent = useRecentFoods()
  const actions: { k: Mode['k']; label: string; icon: string; needsNet?: boolean }[] = [
    { k: 'scan', label: 'Scan barcode', icon: '▥', needsNet: false },
    { k: 'photo', label: 'Meal photo', icon: '◉', needsNet: true },
    { k: 'describe', label: 'Describe', icon: '✎', needsNet: true },
    { k: 'label', label: 'Nutrition label', icon: '▤', needsNet: true },
    { k: 'quick', label: 'Quick add', icon: '+' },
    { k: 'library', label: 'Recipes & meals', icon: '☰' },
  ]
  return (
    <div>
      <div className="pill-row" style={{ marginTop: '.75rem' }}>
        {names.map((n, i) => (
          <button key={n} className={`pill ${i === meal ? 'on' : ''}`} onClick={() => setMeal(i)}>{n}</button>
        ))}
      </div>
      <input style={{ marginTop: '.9rem' }} placeholder="Search foods" onFocus={onSearch} readOnly aria-label="Search foods" />
      <div className="grid3" style={{ marginTop: '.75rem' }}>
        {actions.map((a) => (
          <button key={a.k} className="secondary" disabled={a.needsNet && !online} aria-label={a.label}
            style={{ margin: 0, padding: '.7rem .3rem', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '.2rem',
              fontSize: '.8rem', borderColor: 'var(--border)', color: 'var(--text)' }}
            onClick={() => go(a.k)}>
            <span aria-hidden style={{ fontSize: '1.2rem', color: 'var(--accent)' }}>{a.icon}</span>
            {a.label}
          </button>
        ))}
      </div>
      {!online && <p className="muted small">Offline — photo, describe and label need a connection. Your own foods and recents still work.</p>}

      <h2>Recent</h2>
      {recent === undefined ? null : recent.length === 0 ? (
        <p className="muted small">Foods you log show up here for one-tap re-logging.</p>
      ) : (
        <div className="card" style={{ padding: '.2rem .4rem' }}>
          {recent.slice(0, 15).map((r) => {
            const item = recentToItem(r)
            const kcal = r.nutrients?.kcal
            return (
              <div key={r.key} className="food-row tappable" role="button" tabIndex={0}
                onClick={() => item ? onPick(item) : undefined}
                style={{ opacity: item ? 1 : 0.6 }}>
                <div style={{ minWidth: 0 }}>
                  <div className="name">{r.name}</div>
                  <div className="muted small">{[r.brand, r.serving_label && r.serving_qty ? `${r.serving_qty} × ${r.serving_label}` : r.grams ? `${Math.round(r.grams)} g` : null].filter(Boolean).join(' · ')}</div>
                </div>
                <span className="num muted small">{kcal !== undefined ? `${fmtKcal(kcal)} kcal` : ''}</span>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

// --- search -------------------------------------------------------------------

export function Search({ online, onPick, onMeal, onCreate, onDescribe }: {
  online: boolean; onPick: (i: FoodItem) => void; onMeal: (m: SavedMealRow) => void; onCreate: (name: string) => void
  /** Hand the query to the AI text estimate. Absent where an estimate makes no sense (recipe editor). */
  onDescribe?: (text: string) => void
}) {
  const [q, setQ] = useState('')
  const [remote, setRemote] = useState<FoodItem[] | null>(null)
  const [loading, setLoading] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const custom = useCustomFoods()
  const recipes = useRecipes()
  const meals = useSavedMeals()
  const recent = useRecentFoods()
  const input = useRef<HTMLInputElement>(null)
  useEffect(() => { input.current?.focus() }, [])

  const needle = q.trim().toLowerCase()
  const local = useMemo(() => {
    if (needle.length < 1) return { foods: [] as FoodItem[], meals: [] as SavedMealRow[] }
    const match = (s: string | null | undefined) => (s ?? '').toLowerCase().includes(needle)
    const foods: FoodItem[] = [
      ...(custom ?? []).filter((c) => match(c.name) || match(c.brand)).map(customToItem),
      ...(recipes ?? []).filter((r) => match(r.name)).map(recipeToItem),
      ...(recent ?? []).filter((r) => match(r.name) && r.source !== 'custom' && r.source !== 'recipe')
        .map(recentToItem).filter((x): x is FoodItem => !!x),
    ]
    return { foods: foods.slice(0, 20), meals: (meals ?? []).filter((m) => match(m.name)).slice(0, 5) }
  }, [needle, custom, recipes, meals, recent])

  useEffect(() => {
    setErr(null)
    if (needle.length < 2 || !online) { setRemote(null); return }
    const t = setTimeout(async () => {
      setLoading(true)
      try {
        const r = await withTimeout(api<{ results: FoodItem[] }>(`/foods/search?q=${encodeURIComponent(needle)}`), 15000, 'search')
        setRemote(r.results)
      } catch (e) {
        setErr(errMsg(e))
        setRemote(null)
      } finally { setLoading(false) }
    }, 400)
    return () => clearTimeout(t)
  }, [needle, online])

  const row = (item: FoodItem, key: string) => {
    const k = item.per100.kcal
    // Only a serving the source calls THE serving; CNF's household measures
    // ("100ml slices") are choices for the picker, not a headline.
    const s = item.servings.find((x) => x.default)
    return (
      <div key={key} className="food-row tappable" role="button" tabIndex={0} onClick={() => onPick(item)}>
        <div style={{ minWidth: 0 }}>
          <div className="name">{item.name}</div>
          <div className="muted small">{[item.brand, sourceLabel(item.source), item.country === 'US' ? 'US menu' : null].filter(Boolean).join(' · ')}</div>
        </div>
        <span className="num muted small" style={{ textAlign: 'right' }}>
          {s ? `${fmtKcal(scale(item.per100, s.grams).kcal ?? 0)} / ${s.label.slice(0, 14)}` : k !== undefined ? `${fmtKcal(k)} / 100 g` : ''}
        </span>
      </div>
    )
  }

  return (
    <div>
      <input ref={input} style={{ marginTop: '.75rem' }} value={q} onChange={(e) => setQ(e.target.value)}
        placeholder="e.g. banana, greek yogurt, chicken thigh" aria-label="Search foods" />
      {needle && (local.foods.length > 0 || local.meals.length > 0) && (
        <>
          <h2>Yours</h2>
          <div className="card" style={{ padding: '.2rem .4rem' }}>
            {local.meals.map((m) => (
              <div key={m.id} className="food-row tappable" role="button" tabIndex={0} onClick={() => onMeal(m)}>
                <div><div className="name">{m.name}</div><div className="muted small">Saved meal · {(m.items ?? []).length} items</div></div>
                <span className="num muted small">{fmtKcal(sum((m.items ?? []).map((i) => i.nutrients)).kcal ?? 0)} kcal</span>
              </div>
            ))}
            {local.foods.map((f, i) => row(f, `l${i}`))}
          </div>
        </>
      )}
      {needle.length >= 2 && (
        <>
          <h2>Food database</h2>
          {!online ? <p className="muted small">Offline — the database needs a connection.</p>
            : loading && !remote ? <p className="muted small">Searching…</p>
            : err ? <p className="muted small">{err}</p>
            : remote && remote.length === 0 ? <p className="muted small">No matches.</p>
            : remote && <div className="card" style={{ padding: '.2rem .4rem' }}>{remote.map((f, i) => row(f, `r${i}`))}</div>}
          {remote && remote.length > 0 && <p className="muted small" style={{ fontSize: '.7rem' }}>
            Health Canada Canadian Nutrient File (Open Government Licence – Canada) · Open Food Facts (ODbL) ·
            USDA FoodData Central · restaurants' own published nutrition guides</p>}
        </>
      )}
      {needle && onDescribe && online && <button className="secondary" style={{ width: '100%' }} onClick={() => onDescribe(q.trim())}>
        Not here? Estimate “{q.trim()}” with AI</button>}
      {needle && <button className="secondary" style={{ width: '100%' }} onClick={() => onCreate(q.trim())}>
        Create “{q.trim()}” as your own food</button>}
    </div>
  )
}

// --- barcode -------------------------------------------------------------------

function ScanStep({ online, onFound, onMiss, setError }: {
  online: boolean; onFound: (i: FoodItem) => void; onMiss: (code: string) => void; setError: (e: string | null) => void
}) {
  const [looking, setLooking] = useState<string | null>(null)
  const onCode = useCallback(async (code: string) => {
    setLooking(code)
    // Your own foods first: a product you've entered before scans offline.
    const mine = await db.custom_foods.where('barcode').equals(code).filter((r) => !r.deleted_at).first()
    if (mine) { onFound(customToItem(mine)); return }
    if (!online) { setError('Offline — only your own foods can be scanned right now.'); setLooking(null); return }
    try {
      const r = await withTimeout(api<{ found: boolean; food: FoodItem | null }>(`/foods/barcode/${code}`), 15000, 'barcode')
      if (r.found && r.food) onFound(r.food)
      else onMiss(code)
    } catch (e) {
      setError(errMsg(e))
      setLooking(null)
    }
  }, [online, onFound, onMiss, setError])

  if (looking) return <p className="muted">Looking up {looking}…</p>
  return <Scanner onCode={onCode} />
}

// --- AI: photo / describe --------------------------------------------------------

function EstimateStep({ photo, online, busy, setBusy, setError, onEstimate, initialText }: {
  photo: boolean; online: boolean; busy: boolean; setBusy: (b: boolean) => void; initialText?: string
  setError: (e: string | null) => void; onEstimate: (e: Estimate, s: 'ai_photo' | 'ai_text') => void
}) {
  // Survives the round trip to the camera app, which can reload the WebView.
  const [text, setText] = useState(() => {
    if (initialText) return initialText
    try { return sessionStorage.getItem(PENDING_KEY) ?? '' } catch { return '' }
  })
  const [file, setFile] = useState<File | null>(null)
  const [preview, setPreview] = useState<string | null>(null)
  useEffect(() => { try { sessionStorage.setItem(PENDING_KEY, text) } catch { /* ignore */ } }, [text])
  useEffect(() => () => { if (preview) URL.revokeObjectURL(preview) }, [preview])

  const go = async () => {
    setError(null)
    setBusy(true)
    try {
      const body: Record<string, unknown> = { description: text.trim() || undefined }
      if (photo && file) body.image_base64 = await prepareImage(file, MEAL)
      const r = await withTimeout(api<Estimate>('/ai/estimate', { body }), 90000, 'estimate')
      if (r.status !== 'ok' || r.items.length === 0) {
        setError(r.message || (r.status === 'no_food' ? "That doesn't look like food." : "Couldn't make that out."))
        return
      }
      onEstimate(r, photo && file ? 'ai_photo' : 'ai_text')
    } catch (e) {
      setError(errMsg(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div>
      {photo && (
        <>
          <div className="row" style={{ gap: '.5rem', marginTop: '.75rem' }}>
            <label className="pill on" style={{ margin: 0, flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', cursor: 'pointer' }}>
              Take photo
              <input type="file" accept="image/*" capture="environment" hidden
                onChange={(e) => { const f = e.target.files?.[0]; if (f) { setFile(f); setPreview(URL.createObjectURL(f)) } }} />
            </label>
            <label className="pill" style={{ margin: 0, flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', cursor: 'pointer' }}>
              Choose photo
              <input type="file" accept="image/*" hidden
                onChange={(e) => { const f = e.target.files?.[0]; if (f) { setFile(f); setPreview(URL.createObjectURL(f)) } }} />
            </label>
          </div>
          {preview && <img src={preview} alt="Your meal" style={{ width: '100%', borderRadius: 10, marginTop: '.75rem', maxHeight: 260, objectFit: 'cover' }} />}
          <p className="muted small">The photo is sent once to estimate, then discarded — it's never stored.</p>
        </>
      )}
      <label htmlFor="desc">{photo ? 'Anything the photo won\'t show? (optional)' : 'What did you eat?'}</label>
      <textarea id="desc" value={text} onChange={(e) => setText(e.target.value)} maxLength={1000}
        placeholder={photo ? 'e.g. cooked in 1 tbsp olive oil, ate about half' : 'e.g. 2 scrambled eggs, 1 slice sourdough with butter, a black coffee'} />
      <button style={{ width: '100%' }} disabled={!online || busy || (photo ? !file : !text.trim())} onClick={go}>
        {busy ? 'Estimating… (10–30 s)' : 'Estimate'}
      </button>
      {!online && <p className="muted small">Needs a connection.</p>}
    </div>
  )
}

// --- AI: label ---------------------------------------------------------------------

function LabelStep({ online, barcode, busy, setBusy, setError, onRead }: {
  online: boolean; barcode?: string | null; busy: boolean; setBusy: (b: boolean) => void
  setError: (e: string | null) => void; onRead: (d: CustomFoodDraft, note: string) => void
}) {
  const read = async (file: File) => {
    setError(null)
    setBusy(true)
    try {
      const image_base64 = await prepareImage(file, LABEL)
      const r = await withTimeout(api<{ status: string; message: string; food?: FoodItem }>('/ai/label',
        { body: { image_base64, barcode: barcode ?? undefined } }), 90000, 'label')
      if (r.status !== 'ok' || !r.food) { setError(r.message || "Couldn't read that label."); return }
      const f = r.food
      const s = f.servings[0]
      onRead({ name: f.name, brand: f.brand, barcode: barcode ?? f.barcode ?? null, basis: f.basis,
        serving_label: s?.label ?? '', serving_size: s?.grams ?? 100, per100: f.per100, source: 'label' },
      `${r.message} Check the numbers against the label, add a name, then save.`)
    } catch (e) {
      setError(errMsg(e))
    } finally {
      setBusy(false)
    }
  }
  return (
    <div>
      <p className="muted small" style={{ marginTop: '.75rem' }}>
        Get the whole Nutrition Facts table in frame, flat and in focus. The numbers are copied exactly, not estimated.
      </p>
      <label className={`pill ${online && !busy ? 'on' : ''}`} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', cursor: online ? 'pointer' : 'default', marginTop: '.75rem', minHeight: 44 }}>
        {busy ? 'Reading the label…' : 'Photograph the label'}
        <input type="file" accept="image/*" capture="environment" hidden disabled={!online || busy}
          onChange={(e) => { const f = e.target.files?.[0]; if (f) void read(f); e.target.value = '' }} />
      </label>
      <label className="pill" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', cursor: 'pointer', marginTop: '.5rem', minHeight: 44 }}>
        Choose a photo
        <input type="file" accept="image/*" hidden disabled={!online || busy}
          onChange={(e) => { const f = e.target.files?.[0]; if (f) void read(f); e.target.value = '' }} />
      </label>
      {!online && <p className="muted small">Needs a connection.</p>}
    </div>
  )
}

// --- quick add ------------------------------------------------------------------------

function QuickAdd({ busy, onLog }: { busy: boolean; onLog: (name: string, n: Record<string, number>) => void }) {
  const [name, setName] = useState('')
  const [v, setV] = useState({ kcal: '', protein_g: '', carbs_g: '', fat_g: '' })
  const n = Object.fromEntries(Object.entries(v).filter(([, x]) => x !== '' && Number.isFinite(Number(x)))
    .map(([k, x]) => [k, Number(x)]))
  const implied = 4 * (n.protein_g ?? 0) + 4 * (n.carbs_g ?? 0) + 9 * (n.fat_g ?? 0)
  const kcal = n.kcal ?? (implied || undefined)
  return (
    <div>
      <label htmlFor="qa-name">Label (optional)</label>
      <input id="qa-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Restaurant lunch" />
      <div className="grid2">
        {([['kcal', 'Calories'], ['protein_g', 'Protein (g)'], ['carbs_g', 'Carbs (g)'], ['fat_g', 'Fat (g)']] as const).map(([k, l]) => (
          <div key={k}>
            <label htmlFor={`qa-${k}`}>{l}</label>
            <input id={`qa-${k}`} inputMode="decimal" value={v[k]} onChange={(e) => setV((s) => ({ ...s, [k]: e.target.value }))} />
          </div>
        ))}
      </div>
      {!n.kcal && implied > 0 && <p className="muted small">Calories from macros: {Math.round(implied)} kcal</p>}
      <button style={{ width: '100%' }} disabled={busy || !kcal} onClick={() => onLog(name.trim(), { ...n, kcal: kcal! })}>Log</button>
    </div>
  )
}

// --- recipes & saved meals ----------------------------------------------------------------

function Library({ onRecipe, onMeal }: { onRecipe: (r: RecipeRow) => void; onMeal: (m: SavedMealRow) => void }) {
  const recipes = useRecipes()
  const meals = useSavedMeals()
  const { goTo } = useApp()
  return (
    <div>
      <h2>Saved meals</h2>
      {(meals ?? []).length === 0 ? <p className="muted small">Save a meal from the log (··· on a meal) to re-log it in one tap.</p> : (
        <div className="card" style={{ padding: '.2rem .4rem' }}>
          {(meals ?? []).map((m) => (
            <div key={m.id} className="food-row tappable" role="button" tabIndex={0} onClick={() => onMeal(m)}>
              <div><div className="name">{m.name}</div><div className="muted small">{(m.items ?? []).length} items</div></div>
              <span className="num muted small">{fmtKcal(sum((m.items ?? []).map((i) => i.nutrients)).kcal ?? 0)} kcal</span>
            </div>
          ))}
        </div>
      )}
      <h2>Recipes</h2>
      {(recipes ?? []).length === 0 ? <p className="muted small">No recipes yet.</p> : (
        <div className="card" style={{ padding: '.2rem .4rem' }}>
          {(recipes ?? []).map((r) => (
            <div key={r.id} className="food-row tappable" role="button" tabIndex={0} onClick={() => onRecipe(r)}>
              <div><div className="name">{r.name}</div><div className="muted small">{r.servings} serving{r.servings === 1 ? '' : 's'}</div></div>
              <span className="num muted small">{fmtKcal(r.per_serving?.kcal ?? 0)} kcal / serving</span>
            </div>
          ))}
        </div>
      )}
      <button className="secondary" style={{ width: '100%' }} onClick={() => goTo('more')}>Build a recipe (More → Recipes)</button>
    </div>
  )
}

function MealStep({ meal, busy, onLog }: { meal: SavedMealRow; busy: boolean; onLog: () => void }) {
  const t = sum((meal.items ?? []).map((i) => i.nutrients))
  return (
    <div>
      <p style={{ fontWeight: 600 }}>{meal.name}</p>
      <div className="card" style={{ padding: '.2rem .4rem' }}>
        {(meal.items ?? []).map((i, k) => (
          <div key={k} className="food-row">
            <div><div className="name">{i.name}</div><div className="muted small">{i.grams ? `${Math.round(i.grams)} g` : ''}</div></div>
            <span className="num muted small">{fmtKcal(i.nutrients.kcal ?? 0)} kcal</span>
          </div>
        ))}
      </div>
      <p className="num">Total {fmtKcal(t.kcal ?? 0)} kcal</p>
      <button style={{ width: '100%' }} disabled={busy} onClick={onLog}>Log all</button>
    </div>
  )
}

